import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import ScanConfirmModal from "./store/components/ScanConfirmModal";
import {
  getLoadableShipments,
  getShipmentPackets,
  loadPackage,
} from "./store/production/service";
import { parsePackageScan } from "./store/utils/printPackageStickerLabel";
import { toErrorText } from "./store/pages/storeSalesDashboardHelpers";
import {
  useQrScanner,
  QR_SCANNER_CONTAINER_CLASS,
} from "./store/utils/useQrScanner";

const SCANNER_ELEMENT_ID = "package-scan-qr-reader";

const shipmentDisplayLabel = (shipment) => {
  const suffix =
    shipment.project_internal_id != null
      ? `#${shipment.project_internal_id}`
      : "";
  const projectLabel = shipment.project_name
    ? `${shipment.project_name} ${suffix}`.trim()
    : suffix;

  return shipment.name ? `${projectLabel} — ${shipment.name}` : projectLabel;
};

const sortPackets = (packets) =>
  [...packets].sort((a, b) => {
    if (a.status === "loaded" && b.status !== "loaded") return -1;
    if (a.status !== "loaded" && b.status === "loaded") return 1;

    return String(a.label).localeCompare(String(b.label));
  });

const PackageScan = () => {
  const showTestScanInput = true;
  const lastHandledScanRef = useRef(null);
  const handlePackageScanRef = useRef(null);

  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [modalOpened, setModalOpened] = useState(false);
  const [packets, setPackets] = useState([]);
  const [packetsLoading, setPacketsLoading] = useState(false);
  const [packetsError, setPacketsError] = useState(null);
  const [scanMessage, setScanMessage] = useState(null);
  const [testScanText, setTestScanText] = useState("");
  const [pendingScan, setPendingScan] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const loadShipments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await getLoadableShipments();
      setShipments(rows);
    } catch (err) {
      setShipments([]);
      setError(toErrorText(err, "Unable to load shipments."));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPackets = useCallback(async (shipmentId) => {
    if (!shipmentId) return;

    setPacketsLoading(true);
    setPacketsError(null);
    try {
      const rows = await getShipmentPackets(shipmentId);
      setPackets(rows);
    } catch (err) {
      setPackets([]);
      setPacketsError(toErrorText(err, "Unable to load packets."));
    } finally {
      setPacketsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShipments();
  }, [loadShipments]);

  useEffect(() => {
    if (!selectedShipment) {
      setPackets([]);
      setPacketsError(null);
      setScanMessage(null);
      setPendingScan(null);
      return;
    }

    loadPackets(selectedShipment.id);
  }, [loadPackets, selectedShipment]);

  const scannerEnabled =
    modalOpened && selectedShipment != null && pendingScan == null;

  const { scannerReady, scannerError, clearScannerError } = useQrScanner({
    elementId: SCANNER_ELEMENT_ID,
    enabled: scannerEnabled,
    onScan: (decodedText) => {
      handlePackageScanRef.current?.(decodedText);
    },
  });

  const sortedPackets = useMemo(() => sortPackets(packets), [packets]);

  const loadedPackageCount = useMemo(
    () => packets.filter((packet) => packet.status === "loaded").length,
    [packets]
  );
  const closedOrLoadedPackageCount = useMemo(
    () =>
      packets.filter(
        (packet) => packet.status === "closed" || packet.status === "loaded"
      ).length,
    [packets]
  );

  const handlePackageScan = useCallback(
    (decodedText) => {
      const trimmed = String(decodedText ?? "").trim();
      if (!trimmed || trimmed === lastHandledScanRef.current) return;
      if (!selectedShipment) return;

      setScanMessage(null);
      lastHandledScanRef.current = trimmed;
      setPendingScan({ text: trimmed });
    },
    [selectedShipment]
  );

  handlePackageScanRef.current = handlePackageScan;

  const cancelConfirm = () => {
    if (confirmLoading) return;

    setPendingScan(null);
    lastHandledScanRef.current = null;
  };

  const confirmScan = async () => {
    if (!pendingScan || !selectedShipment || confirmLoading) return;

    const { text } = pendingScan;
    const parsed = parsePackageScan(text);

    if (!parsed) {
      setPendingScan(null);
      lastHandledScanRef.current = null;
      setScanMessage({
        type: "warning",
        message: "Invalid package scan format.",
      });
      return;
    }

    const { packageId: packageIdRaw, packageLabel } = parsed;
    const packageId = Number(packageIdRaw);
    const packet = packets.find(
      (p) => p.id === packageId && p.label === packageLabel
    );

    if (!packet) {
      setPendingScan(null);
      lastHandledScanRef.current = null;
      setScanMessage({
        type: "danger",
        message: "Package not found on this shipment.",
      });
      return;
    }

    const shipmentId = selectedShipment.id;
    setConfirmLoading(true);

    try {
      await loadPackage(packet.id, { label: packageLabel });
      await loadPackets(shipmentId);
      setPendingScan(null);
    } catch (err) {
      setPendingScan(null);
      lastHandledScanRef.current = null;
      setScanMessage({
        type: "danger",
        message: toErrorText(err, "Unable to load package."),
      });
    } finally {
      setConfirmLoading(false);
    }
  };

  const closeModal = () => {
    if (confirmLoading) return;

    setSelectedShipment(null);
    setModalOpened(false);
    setPendingScan(null);
    lastHandledScanRef.current = null;
    setTestScanText("");
    setScanMessage(null);
    clearScannerError();
  };

  const resetForRescan = () => {
    lastHandledScanRef.current = null;
    setScanMessage(null);
    setPendingScan(null);
  };

  const handleTestScanSubmit = () => {
    const text = testScanText.trim();
    if (!text) return;

    lastHandledScanRef.current = null;
    handlePackageScan(text);
    setTestScanText("");
  };

  const modalTitle = selectedShipment
    ? shipmentDisplayLabel(selectedShipment)
    : "Load packages";

  return (
    <div className="min-vh-100 w-100 bg-light">
      <div className="container py-4">
        {error ? (
          <Alert color="danger" fade={false} className="mb-4">
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <div className="py-5 text-center text-muted">
            <Spinner className="me-2" />
            Loading…
          </div>
        ) : shipments.length === 0 ? (
          <p className="text-muted mb-0">No shipments ready to load.</p>
        ) : (
          <div className="row row-cols-1 row-cols-md-3 g-3">
            {shipments.map((shipment) => (
              <div key={shipment.id} className="col">
                <Card
                  className="h-100 shadow-sm"
                  role="button"
                  onClick={() => setSelectedShipment(shipment)}
                >
                  <CardBody>
                    <div className="text-dark fw-semibold">
                      {shipmentDisplayLabel(shipment)}
                    </div>
                    <div className="text-muted small mt-2">
                      <div className="text-capitalize">
                        Status: {shipment.status}
                      </div>
                      <div>Packages: {shipment.packages_count}</div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        isOpen={selectedShipment != null}
        toggle={closeModal}
        fullscreen
        onOpened={() => setModalOpened(true)}
        onClosed={() => setModalOpened(false)}
      >
        <ModalHeader toggle={closeModal}>
          <h6 className="m-0">{modalTitle}</h6>
        </ModalHeader>
        <ModalBody>
          {packetsError ? (
            <Alert color="danger" fade={false} className="mb-3">
              {packetsError}
            </Alert>
          ) : null}

          {scanMessage ? (
            <Alert color={scanMessage.type} fade={false} className="mb-3">
              {scanMessage.message}
            </Alert>
          ) : null}

          {selectedShipment ? (
            <>
              {scannerError ? (
                <Alert color="warning" fade={false} className="mb-3">
                  {scannerError}
                </Alert>
              ) : null}

              <div className="rounded border bg-white shadow-sm mb-4 p-3">
                <div className="fw-semibold mb-2">Camera</div>
                {!scannerReady && !scannerError ? (
                  <p className="text-muted mb-3">Starting camera…</p>
                ) : null}
                <div
                  id={SCANNER_ELEMENT_ID}
                  className={QR_SCANNER_CONTAINER_CLASS}
                />
              </div>

              {showTestScanInput ? (
                <div className="rounded border bg-white shadow-sm mb-4 p-3">
                  <div className="fw-semibold mb-2">Test scan</div>
                  <div className="d-flex flex-wrap gap-2 align-items-end">
                    <div className="flex-grow-1">
                      <label
                        htmlFor="package-scan-test-input"
                        className="form-label small text-muted mb-1"
                      >
                        Scan text
                      </label>
                      <input
                        id="package-scan-test-input"
                        type="text"
                        className="form-control font-monospace"
                        value={testScanText}
                        disabled={confirmLoading || Boolean(pendingScan)}
                        onChange={(event) =>
                          setTestScanText(event.target.value)
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleTestScanSubmit();
                          }
                        }}
                      />
                    </div>
                    <Button
                      color="primary"
                      outline
                      disabled={
                        confirmLoading ||
                        Boolean(pendingScan) ||
                        !testScanText.trim()
                      }
                      onClick={handleTestScanSubmit}
                    >
                      Submit
                    </Button>
                    <Button
                      color="light"
                      outline
                      disabled={confirmLoading}
                      onClick={resetForRescan}
                    >
                      Scan again
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}

          {packetsLoading ? (
            <div className="py-4 text-center text-muted">
              <Spinner className="me-2" />
              Loading…
            </div>
          ) : null}

          {!packetsLoading && selectedShipment ? (
            <div>
              <div className="fw-bold mb-3 fs-6">
                {loadedPackageCount} packages loaded out of{" "}
                {closedOrLoadedPackageCount}
              </div>
              <div className="fw-semibold mb-2">Packages</div>
              {sortedPackets.length === 0 ? (
                <p className="text-muted mb-0">No packages found.</p>
              ) : (
                <div className="d-flex flex-wrap gap-2">
                  {sortedPackets.map((packet) => (
                    <div
                      key={packet.id}
                      className={`rounded border shadow-sm flex-shrink-0 text-center${
                        packet.status === "loaded"
                          ? " bg-success-subtle border-success-subtle"
                          : " bg-white"
                      }`}
                      style={{ width: "120px" }}
                    >
                      <div className="p-2 font-monospace small">
                        {packet.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            disabled={confirmLoading}
            onClick={closeModal}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <ScanConfirmModal
        isOpen={pendingScan != null}
        scanText={pendingScan?.text ?? ""}
        title="Confirm package scan"
        confirmLabel="Load package"
        loading={confirmLoading}
        onConfirm={confirmScan}
        onCancel={cancelConfirm}
      />
    </div>
  );
};

export default PackageScan;
